// apps/vendor/src/pages/errors/MaintenancePage.tsx
//
// UAT #2658 — full-screen maintenance surface for the vendor portal. Reached
// when the BE global maintenance gate returns 503 MAINTENANCE_MODE_ACTIVE; the
// csrfFetch wrapper (lib/api/csrfFetch.ts) stashes the operator message in
// sessionStorage and hard-redirects here so the vendor sees a clean
// "under maintenance" screen instead of a clickable shell whose every request
// silently 503s.

import { type JSX } from 'react';

import { useTranslation } from '../../lib/i18n/index.js';

export const MAINTENANCE_MESSAGE_KEY = 'pw_maintenance_msg';

const PRIMARY_BTN_CLS =
  'inline-flex h-9 items-center justify-center rounded-md bg-pw-primary px-4 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pw-primary focus-visible:ring-offset-1';

function reloadPage(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(MAINTENANCE_MESSAGE_KEY);
  } catch {
    /* sessionStorage unavailable — ignore */
  }
  window.location.assign('/dashboard');
}

function readStoredMessage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem(MAINTENANCE_MESSAGE_KEY);
    return v && v.trim().length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function MaintenancePage(): JSX.Element {
  const { t } = useTranslation('common');
  const custom = readStoredMessage();

  return (
    <div
      data-testid="error-page-maintenance"
      className="flex min-h-screen flex-col items-center justify-center bg-pw-surface px-4 py-12 text-center"
    >
      <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-3xl">
        <span aria-hidden>🛠️</span>
      </div>
      <h1 className="mt-6 text-2xl font-semibold text-pw-text">
        {t('errors.maintenance.title')}
      </h1>
      <p className="mt-3 max-w-md text-sm text-pw-text-muted">
        {custom ?? t('errors.maintenance.subtitle')}
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={reloadPage}
          data-testid="maintenance-retry"
          className={PRIMARY_BTN_CLS}
        >
          {t('errors.maintenance.retry')}
        </button>
      </div>
    </div>
  );
}

export default MaintenancePage;
